import React, { useEffect, useState } from "react";
import { ukDayTime } from "@/lib/time";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading } from "@/components/hub/Shell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PurgeButton } from "@/components/hub/PurgeButton";
import {
  Loader2, CheckCircle2, XCircle, AlertTriangle, FileText, Download, QrCode, Send,
  ExternalLink, ShieldCheck, Inbox,
} from "lucide-react";

const money = (n) => `£${Math.round(Number(n) || 0).toLocaleString()}`;
const fmtDate = (iso) => ukDayTime(iso, "—");
const prettySize = (b) => (!b ? "" : b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1048576).toFixed(1)} MB`);

const announce = () => window.dispatchEvent(new Event("customer-apps:changed"));

const URGENCY_LABEL = {
  urgent: "As soon as possible", this_week: "Within a week",
  this_month: "Within a month", flexible: "Flexible / exploring",
};

const VIA = {
  customer_link:   { label: "Introducer's own link", sentence: "link or QR code", Icon: QrCode },
  customer_invite: { label: "Tracked invite",        sentence: "tracked invite",  Icon: Send },
};

const FILTERS = [
  { key: "pending",  label: "Needs review" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all",      label: "All" },
];

/**
 * CustomerAppsPanel — applications completed by the customer themselves through an
 * introducer's link. Nothing reaches a lender until it's approved here.
 */
export default function CustomerAppsPanel({ compact = false }) {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState({ items: [], pending: 0, auto_route: false, enabled: true });
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/hub/customer-applications?status=${compact ? "pending" : status}&limit=${compact ? 5 : 60}`);
      setData(r.data);
    } catch { setData({ items: [], pending: 0, auto_route: false, enabled: true }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status, compact]); // eslint-disable-line
  useEffect(() => {
    window.addEventListener("customer-apps:changed", load);
    return () => window.removeEventListener("customer-apps:changed", load);
  }); // eslint-disable-line

  const items = data.items || [];
  const shown = compact ? items.slice(0, 4) : items;

  if (compact && !loading && (data.pending || 0) === 0) return null;

  return (
    <div className={compact ? "" : "space-y-4"} data-testid={compact ? "customer-apps-compact" : "customer-apps-panel"}>
      {compact ? (
        <div className="rounded-2xl border border-[#7da9ff]/60 bg-[#7da9ff]/[0.10] overflow-hidden">
          <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap border-b border-[#7da9ff]/45">
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-[#3B63A8]" />
              <div className="text-[13.5px] font-semibold text-[#24457F]">
                {data.pending} customer application{data.pending === 1 ? "" : "s"} waiting for your review
              </div>
            </div>
            <Link to="/hub/customer-apps"
                  className="text-[12.5px] font-semibold text-[#24457F] underline underline-offset-2 hover:text-[#3B63A8]"
                  data-testid="customer-apps-review-all">
              Review all
            </Link>
          </div>
          <div className="bg-white divide-y divide-slate-100">
            {shown.map((a) => <Row key={a.id} app={a} onReview={setReviewing} onDone={load} compact />)}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map((f) => (
              <button key={f.key} type="button" onClick={() => setStatus(f.key)}
                      className={`inline-flex items-center gap-1.5 rounded-[10px] border px-3 h-8 text-[12.5px] font-semibold transition-colors ${
                        status === f.key ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                      data-testid={`customer-apps-filter-${f.key}`}>
                {f.label}
                {f.key === "pending" && (data.pending || 0) > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full text-[10px] font-bold ${
                    status === "pending" ? "bg-white text-slate-900" : "bg-[#0055FF] text-white"}`}>{data.pending}</span>
                )}
              </button>
            ))}
          </div>

          {!data.enabled && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[12.5px] text-slate-600" data-testid="customer-apps-closed">
              Customer application links are switched <b>off</b> — existing links show a polite "closed" message.
              Turn them back on from the Automation card on Applications.
            </div>
          )}
          {data.auto_route && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-[12.5px] text-blue-800" data-testid="customer-apps-autoroute">
              <b>Auto-route is on.</b> New customer applications go straight to matching lenders — this list is a record, not a gate.
            </div>
          )}

          {loading ? (
            <Loading />
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white text-center py-16 px-6" data-testid="customer-apps-empty">
              <Inbox className="size-6 text-slate-400 mx-auto" />
              <div className="mt-3 text-[15px] font-semibold text-slate-900">
                {status === "pending" ? "Nothing waiting — you're clear" : `No ${status === "all" ? "" : status} customer applications`}
              </div>
              <p className="mt-1 text-[13px] text-slate-500 max-w-md mx-auto">
                Introducers send their customers a private link from their portal. Whatever they submit lands here first.
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden divide-y divide-slate-100">
              {shown.map((a) => <Row key={a.id} app={a} onReview={setReviewing} onDone={load} />)}
            </div>
          )}
        </>
      )}

      <ReviewDialog app={reviewing} onOpenChange={(v) => !v && setReviewing(null)}
                    onDone={() => { setReviewing(null); load(); }} />
    </div>
  );
}

function Row({ app, onReview, onDone, compact = false }) {
  const [busy, setBusy] = useState(false);
  const via = VIA[app.submitted_via] || VIA.customer_link;
  const pending = app.review_status === "pending";

  const approve = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.post(`/hub/customer-applications/${app.id}/approve`);
      toast.success(r.data?.routed
        ? `Approved — sent to ${r.data.routes_added} lender${r.data.routes_added === 1 ? "" : "s"}${r.data.rule ? ` (${r.data.rule})` : ""}`
        : "Approved — no auto-route rule matched, pick lenders on the deal");
      onDone?.();
      announce();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Could not approve that"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 flex items-start gap-3 hover:bg-slate-50/60 transition-colors cursor-pointer"
         onClick={() => onReview(app)} data-testid={`customer-app-${app.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[11.5px] font-semibold text-slate-500 tabular-nums">{app.reference || "—"}</span>
          {pending && (
            <span className="inline-flex items-center rounded-full border border-[#0055FF]/30 bg-[#0055FF]/[0.07] text-[#0044CC] px-2 h-5 text-[10px] font-bold uppercase tracking-[0.12em]"
                  data-testid={`customer-app-needs-review-${app.id}`}>Needs review</span>
          )}
          {app.review_status === "approved" && (
            <span className="inline-flex items-center rounded-full border border-teal-200 bg-teal-50 text-teal-700 px-2 h-5 text-[10px] font-bold uppercase tracking-[0.12em]">Approved</span>
          )}
          {app.review_status === "rejected" && (
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 text-slate-500 px-2 h-5 text-[10px] font-bold uppercase tracking-[0.12em]">Rejected</span>
          )}
          {app.duplicate_of && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8] px-2 h-5 text-[10px] font-bold uppercase tracking-[0.12em]"
                  data-testid={`customer-app-duplicate-${app.id}`}>
              <AlertTriangle className="size-2.5" /> Possible duplicate
            </span>
          )}
        </div>
        <div className="mt-1 text-[14px] font-semibold text-slate-900 truncate">
          {app.business_name || app.applicant_name}
        </div>
        <div className="text-[12.5px] text-slate-500 truncate">
          {app.applicant_name}{app.applicant_phone ? ` · ${app.applicant_phone}` : ""}{app.applicant_email ? ` · ${app.applicant_email}` : ""}
        </div>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11.5px] text-slate-400">
          <span className="inline-flex items-center gap-1"><via.Icon className="size-3" /> {via.label}</span>
          <span>·</span>
          <span>{app.direct ? <b className="text-[#0044CC] font-semibold">Direct · website</b>
                            : <>Introduced by <b className="text-slate-600 font-semibold">{app.source_name || "—"}</b></>}</span>
          {(app.files || []).length > 0 && <><span>·</span><span className="inline-flex items-center gap-1"><FileText className="size-3" /> {app.files.length} document{app.files.length === 1 ? "" : "s"}</span></>}
          <span>·</span>
          <span>{fmtDate(app.created_at)}</span>
        </div>
      </div>

      <div className="text-right shrink-0">
        <div className="font-mono tabular-nums text-[15px] font-semibold text-slate-900">{money(app.amount_gbp)}</div>
        <div className="text-[11.5px] text-slate-500">{app.finance_type}</div>
        <div className="mt-2 flex items-center gap-1.5 justify-end">
          {pending && (
            <Button size="sm" onClick={approve} disabled={busy}
                    className="h-8 rounded-[10px] bg-teal-500 hover:bg-teal-600 text-[12px] font-semibold"
                    data-testid={`customer-app-approve-${app.id}`}>
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <><CheckCircle2 className="size-3.5 mr-1" /> Approve</>}
            </Button>
          )}
          {!compact && (
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onReview(app); }}
                    className="h-8 rounded-[10px] text-[12px]" data-testid={`customer-app-open-${app.id}`}>
              Read it
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewDialog({ app, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => { setRejecting(false); setReason(""); }, [app?.id]);

  if (!app) return null;
  const pending = app.review_status === "pending";
  const via = VIA[app.submitted_via] || VIA.customer_link;

  const act = async (what) => {
    setBusy(true);
    try {
      if (what === "approve") {
        const r = await api.post(`/hub/customer-applications/${app.id}/approve`);
        toast.success(r.data?.routed
          ? `Approved — sent to ${r.data.routes_added} lender${r.data.routes_added === 1 ? "" : "s"}`
          : "Approved — no auto-route rule matched, pick lenders on the deal");
      } else {
        await api.post(`/hub/customer-applications/${app.id}/reject`, { reason: reason.trim() || null });
        toast.success("Rejected — the introducer has been told");
      }
      onDone?.();
      announce();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!app} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-app-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {app.business_name || app.applicant_name}
            <span className="font-mono text-[12px] font-semibold text-slate-400 tabular-nums">{app.reference}</span>
          </DialogTitle>
          <DialogDescription>
            {app.direct
              ? <>Completed by the customer on the business funding page · {fmtDate(app.created_at)}</>
              : <>Completed by the customer through {app.source_name ? <b>{app.source_name}</b> : "an introducer"}&apos;s {via.sentence} · {fmtDate(app.created_at)}</>}
          </DialogDescription>
        </DialogHeader>

        {app.duplicate_of && (
          <div className="rounded-xl border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] p-3.5 text-[12.5px] text-[#24457F]" data-testid="customer-app-dialog-duplicate">
            <b>Possible duplicate.</b> The same introducer submitted this customer within the last 30 days.{" "}
            <Link to={`/hub/leads/${app.duplicate_of}`} className="underline font-semibold" onClick={() => onOpenChange(false)}>
              Open the earlier deal
            </Link>{" "}before you approve, so commission is only paid once.
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3 text-[13px]">
          <D label="Applicant" value={app.applicant_name} />
          <D label="Email" value={app.applicant_email} />
          <D label="Phone" value={app.applicant_phone} />
          <D label="Business" value={app.business_name} />
          <D label="Companies House #" value={app.company_number} />
          <D label="Industry" value={app.industry_sector} />
          <D label="Years trading" value={app.trading_years !== null && app.trading_years !== undefined ? `${app.trading_years} years` : null} />
          <D label="Employees" value={app.employees} />
          <D label="Annual turnover" value={app.annual_revenue_gbp ? money(app.annual_revenue_gbp) : null} />
          <D label="Finance type" value={app.finance_type} />
          <D label="Amount" value={money(app.amount_gbp)} />
          <D label="Term" value={app.term_months ? `${app.term_months} months` : null} />
          <D label="Urgency" value={URGENCY_LABEL[app.urgency] || app.urgency} />
          <D label="Purpose" value={app.loan_purpose} full />
          <D label="Existing borrowing" value={app.existing_debt_summary} full />
          <D label="What else they told us" value={app.notes} full />
        </div>

        {(app.files || []).length > 0 && (
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Documents they uploaded</div>
            <div className="mt-2 space-y-1.5">
              {app.files.map((f) => <FileRow key={f.id} file={f} />)}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-teal-200 bg-teal-50/70 p-3 text-[12px] text-teal-800 flex items-start gap-2">
          <ShieldCheck className="size-3.5 mt-0.5 shrink-0" />
          <span>The customer ticked consent for their details to be shared with lenders and funders to arrange finance, timestamped with their IP at submission.</span>
        </div>

        {!pending && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-[12.5px] text-slate-600" data-testid="customer-app-dialog-outcome">
            {app.review_status === "approved"
              ? "Approved — it now behaves like any other deal in the pipeline."
              : `Rejected${app.review_note ? ` — ${app.review_note}` : ""}. The introducer was told.`}
          </div>
        )}

        {pending && rejecting && (
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Why isn&apos;t it proceeding?</div>
            <Textarea className="mt-1.5 min-h-[80px]" value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Same customer already with us via another introducer"
                      data-testid="customer-app-reject-reason" />
            <p className="mt-1.5 text-[11.5px] text-slate-500">This exact wording goes to the introducer, so keep it fair.</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Link to={`/hub/leads/${app.id}`} onClick={() => onOpenChange(false)}
                className="mr-auto inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0055FF] hover:text-[#0044CC]"
                data-testid="customer-app-open-deal">
            Open the full deal <ExternalLink className="size-3.5" />
          </Link>
          <PurgeButton kind="lead" ids={[app.id]} label="Delete"
                       onDone={() => { onOpenChange(false); onDone?.(); }} testId="customer-app-purge" />
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="customer-app-dialog-close">Close</Button>
          {pending && (rejecting ? (
            <Button onClick={() => act("reject")} disabled={busy} className="bg-red-500 hover:bg-red-600" data-testid="customer-app-reject-confirm">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <><XCircle className="size-4 mr-1.5" /> Reject &amp; tell them</>}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setRejecting(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="customer-app-reject-open">
                Not proceeding
              </Button>
              <Button onClick={() => act("approve")} disabled={busy} className="bg-teal-500 hover:bg-teal-600" data-testid="customer-app-dialog-approve">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <><CheckCircle2 className="size-4 mr-1.5" /> Approve &amp; route</>}
              </Button>
            </>
          ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileRow({ file }) {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/hub/files/${file.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: r.data.type || file.content_type }));
      const a = document.createElement("a"); a.href = url; a.download = file.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2.5" data-testid={`customer-app-file-${file.id}`}>
      <FileText className="size-4 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-900 truncate">{file.filename}</div>
        <div className="text-[11px] text-slate-500">{prettySize(file.size)}</div>
      </div>
      <Button variant="ghost" size="sm" onClick={dl} disabled={busy}>
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Download className="size-3.5" />}
      </Button>
    </div>
  );
}

function D({ label, value, full = false }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
      <div className="text-[13px] text-slate-900 mt-0.5 break-words whitespace-pre-wrap">{value}</div>
    </div>
  );
}
